"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATE_FORMATS = void 0;
exports.createArkDayjs = createArkDayjs;
exports.normalizeToMillisecond = normalizeToMillisecond;
const tslib_1 = require("tslib");
/**
 * @ark/dayjs 内部共享工厂
 *
 * 抽离 dayjs 插件注册与「默认时区解析包装」核心逻辑，供后端默认入口与前端入口复用。
 * 本文件零副作用地对外暴露工厂，具体默认时区由调用方按运行环境注入：
 * - 后端：由 @ark/env 的 getTimezone() 提供（见 ./index.ts，历史实现保持不变，未接入本工厂）
 * - 前端：由 window._params.siteId 映射提供（见 ./frontend.ts）
 *
 * 注意：本文件禁止依赖 @ark/env / @futu/appinfo 等后端专用模块，
 * 以保证前端入口 tree-shaking 后不会把后端依赖打进包。
 */
const dayjs_1 = tslib_1.__importDefault(require("dayjs"));
const utc_1 = tslib_1.__importDefault(require("dayjs/plugin/utc"));
const timezone_1 = tslib_1.__importDefault(require("dayjs/plugin/timezone"));
const advancedFormat_1 = tslib_1.__importDefault(require("dayjs/plugin/advancedFormat"));
const isSameOrAfter_1 = tslib_1.__importDefault(require("dayjs/plugin/isSameOrAfter"));
const isSameOrBefore_1 = tslib_1.__importDefault(require("dayjs/plugin/isSameOrBefore"));
const isBetween_1 = tslib_1.__importDefault(require("dayjs/plugin/isBetween"));
// 对外暴露插件的 Dayjs 类型增强，确保依赖方能识别插件方法。
require("dayjs/plugin/utc");
require("dayjs/plugin/timezone");
require("dayjs/plugin/isSameOrAfter");
require("dayjs/plugin/isSameOrBefore");
let pluginsExtended = false;
/**
 * 幂等注册 dayjs 插件（多次调用只生效一次）
 */
function ensurePlugins() {
    if (pluginsExtended)
        return;
    dayjs_1.default.extend(utc_1.default);
    dayjs_1.default.extend(timezone_1.default);
    dayjs_1.default.extend(advancedFormat_1.default);
    dayjs_1.default.extend(isSameOrAfter_1.default);
    dayjs_1.default.extend(isSameOrBefore_1.default);
    dayjs_1.default.extend(isBetween_1.default);
    pluginsExtended = true;
}
const EXPLICIT_TIMEZONE_PATTERN = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;
function isTimezoneNaiveString(date) {
    return typeof date === 'string' && !EXPLICIT_TIMEZONE_PATTERN.test(date);
}
/**
 * 创建一个已配置默认时区的 Ark dayjs 实例。
 *
 * @param defaultTimezone IANA 时区标识符，如 Asia/Hong_Kong、Australia/Sydney
 */
function createArkDayjs(defaultTimezone) {
    ensurePlugins();
    dayjs_1.default.tz.setDefault(defaultTimezone);
    /**
     * 把已解析的 Dayjs 安全转到默认时区。
     *
     * dayjs 的 .tz() 对非法日期会调用 formatToParts 直接抛 RangeError，
     * 因此非法对象原样返回（isValid()===false），保持原生「非法不抛错」语义。
     */
    function toDefaultTimezone(instance) {
        return instance.isValid() ? instance.tz(defaultTimezone) : instance;
    }
    /**
     * 默认时区下的 dayjs 解析。
     *
     * 无时区字符串按展业当地墙钟解析（baseDayjs.tz 解析语义，保证 DST 正确）；
     * 其它输入正常解析后仅做展示时区换算。
     */
    function dayjsInDefaultTimezone(date, format, locale, strict) {
        if (isTimezoneNaiveString(date)) {
            const parsed = (0, dayjs_1.default)(date);
            if (!parsed.isValid()) {
                return parsed;
            }
            return typeof format === 'string'
                ? dayjs_1.default.tz(date, format, defaultTimezone)
                : dayjs_1.default.tz(date, defaultTimezone);
        }
        return toDefaultTimezone((0, dayjs_1.default)(date, format, locale, strict));
    }
    const dayjs = Object.assign(dayjsInDefaultTimezone, dayjs_1.default);
    return {
        dayjs,
        getDefaultTimezone: () => defaultTimezone,
        getTimestamp: () => dayjs().valueOf(),
    };
}
/**
 * 将时间戳标准化为毫秒级
 *
 * @param timestamp 秒级（10位）或毫秒级（13位）时间戳
 * @returns 毫秒级时间戳
 * @throws 如果时间戳格式不正确
 */
function normalizeToMillisecond(timestamp) {
    const timestampStr = timestamp.toString();
    if (timestampStr.length === 13) {
        return Number(timestamp);
    }
    else if (timestampStr.length === 10) {
        return Number(timestamp) * 1000;
    }
    throw new Error('无效的时间戳：时间戳必须是秒级（10位）或毫秒级（13位）');
}
/**
 * 常用时间格式常量
 */
exports.DATE_FORMATS = {
    /** 日期格式：2024-01-20 */
    DATE: 'YYYY-MM-DD',
    /** 时间格式：14:30:00 */
    TIME: 'HH:mm:ss',
    /** 日期时间格式：2024-01-20 14:30:00 */
    DATETIME: 'YYYY-MM-DD HH:mm:ss',
    /** ISO 8601 格式 */
    ISO: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
};
